import { Button, Divider, Grid, Typography } from "@mui/material";
import { Vocabulary } from "../Utils/Vocabulary";
import { localUrlEnum, endpoints } from "../Utils/UrlEnum";
import styles from "../Styles/settings.module.css";
import GeneralSettings from "../Components/Settings/GeneralSettings";
import LegalInfoSettings from "../Components/Settings/LegalInfoSettings";
import DrivingRulesSettings from "../Components/Settings/DrivingRulesSettings";
import { useEffect, useState } from "react";
import { getData } from "../Services/getData";
import { postData } from "../Services/postData";
import ExamQuestionsSettings from "../Components/Settings/ExamQuestionsSettings";
import OCRTokenSettings from "../Components/Settings/OCRToken";

export default function Settings() {
  const generalSettingsButton = [
    {
      title: Vocabulary.companies,
      url: localUrlEnum.company,
    },
    {
      title: Vocabulary.accounts,
      url: localUrlEnum.accounts,
    },
    {
      title: Vocabulary.subscriptions,
      url: localUrlEnum.subscriptions,
    },
    {
      title: Vocabulary.notifications,
      url: localUrlEnum.notificationConfiguration,
    },
  ];
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [examQuestionsModalOpen, setExamQuestionsModalOpen] = useState(false);
  const [OCRTokenModalOpen, setOCRTokenModalOpen] = useState(false);

  const [legalData, setLegalData] = useState<{ legalInfo: string; } | null>(null);
  const [rulesData, setRulesData] = useState<{ drivingRules: string; } | null>(null);
  const [examQuestionsData, setExamQuestionsData] = useState<{ examQuestions: string; } | null>(null);
  const [ocrTokenDataData, setOcrTokenDataData] = useState<{ ocrToken: string; } | null>(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [legalResponse, rulesResponse, examQuestionResponse, ocrTokenResponse] = await Promise.all([
          getData(endpoints.setting.legal),
          getData(endpoints.setting.rules),
          getData(endpoints.setting.examQuestions),
          getData(endpoints.setting.ocrToken),
        ]);

        setLegalData(legalResponse?.data?.value || { legalInfo: "" });
        setRulesData(rulesResponse?.data?.value || { examQuestions: "" });
        setExamQuestionsData(examQuestionResponse?.data?.value || { examQuestions: "" });
        setOcrTokenDataData(ocrTokenResponse?.data?.value || { ocrTokenDataData: "" });
      } catch (error) {
        console.error("Error fetching settings data:", error);
      }
    };

    fetchData();
  }, []);



  const handleUpdateLegalSettings = async () => {
    setLegalModalOpen(false);
    try {
      await postData(endpoints.setting.legal, {
        name: "legalSettings",
        translation: Vocabulary.legalInformation,
        value: {
          ...legalData,
        },
      });
    } catch (error) {
      console.error("Error updating legal data:", error);
    }
  };

  const handleUpdateRulesSettings = async () => {
    setRulesModalOpen(false);
    try {
      await postData(endpoints.setting.rules, {
        name: "ruleSettings",
        translation: Vocabulary.drivingRules,
        value: {
          ...rulesData,
        },
      });
    } catch (error) {
      console.error("Error updating rules data:", error);
    }
  };


  const handleUpdateExamQuestionSettings = async () => {
    setExamQuestionsModalOpen(false);
    try {
      await postData(endpoints.setting.examQuestions, {
        name: "examQuestionSettings",
        translation: Vocabulary.examQuestions,
        value: {
          ...examQuestionsData,
        },
      });
    } catch (error) {
      console.error("Error updating exam data:", error);
    }
  };

  const handleUpdateOcrTokenSettings = async () => {
    setExamQuestionsModalOpen(false);
    try {
      await postData(endpoints.setting.ocrToken, {
        name: "ocrTokenSettings",
        translation: Vocabulary.tokenOCR,
        value: {
          ...ocrTokenDataData,
        },
      });
    } catch (error) {
      console.error("Error updating token data:", error);
    }
  };

  const handleSettingTitle = (title: string) => {
    return (
      <Grid item xs={12} sm={12} md={12}>
        <Typography variant="h5" sx={{ fontWeight: 300 }}>
          {title}
        </Typography>
        <Divider />
      </Grid>
    );
  };

  return (
    <div className={styles.mainContainer} >
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          className={styles.settingContainerGrid}
        >
          <Typography variant="h4">{Vocabulary.settings}</Typography>
        </Grid>
        {/** General Settings */}
        {handleSettingTitle(Vocabulary.general)}
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          className={styles.settingContainerGrid}
        >
          <GeneralSettings />
        </Grid>

        {/** Quick Actions */}
        {handleSettingTitle("Acțiuni rapide")}
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          className={styles.settingContainerGrid}
        >
          <div className={styles.buttonContainer}>
            {generalSettingsButton?.map((button) => {
              return (
                <Button
                  key={button.url}
                  variant="contained"
                  size="large"
                  onClick={() => {
                    window.location.href = button.url;
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 400,
                    minWidth: 150,
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    boxShadow: 'none',
                    border: '1px solid #dee2e6',
                    '&:hover': {
                      backgroundColor: '#e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {button.title}
                </Button>
              );
            })}
          </div>
        </Grid>

        {/** Legal Information Settings */}
        <Grid item xs={12} sm={12} md={4} className={styles.settingContainerGrid}>
          <Button
            variant="contained"
            size="medium"
            fullWidth
            onClick={() => setLegalModalOpen(true)}
          >
            {Vocabulary.legalInformation}
          </Button>
        </Grid>

        {/** Driving Rules Settings */}
        <Grid item xs={12} sm={12} md={4} className={styles.settingContainerGrid}>
          <Button
            variant="contained"
            size="medium"
            fullWidth
            onClick={() => setRulesModalOpen(true)}
          >
            {Vocabulary.drivingRules}
          </Button>
        </Grid>

        {/** ExamQuestion Settings */}
        <Grid item xs={12} sm={12} md={4} className={styles.settingContainerGrid}>
          <Button
            variant="contained"
            size="medium"
            fullWidth
            onClick={() => setExamQuestionsModalOpen(true)}
          >
            {Vocabulary.examQuestions}
          </Button>
        </Grid>

        {/** OCR Token */}
        <Grid item xs={12} sm={12} md={4} className={styles.settingContainerGrid}>
          <Button
            variant="contained"
            size="medium"
            fullWidth
            onClick={() => setOCRTokenModalOpen(true)}
          >
            {Vocabulary.tokenOCR}
          </Button>
        </Grid>

        <LegalInfoSettings
          isOpen={legalModalOpen}
          onClose={() => setLegalModalOpen(false)}
          onSubmit={handleUpdateLegalSettings}
          data={legalData}
          setData={setLegalData}
        />

        <DrivingRulesSettings
          isOpen={rulesModalOpen}
          onClose={() => setRulesModalOpen(false)}
          onSubmit={handleUpdateRulesSettings}
          data={rulesData}
          setData={setRulesData}
        />

        <ExamQuestionsSettings
          isOpen={examQuestionsModalOpen}
          onClose={() => setExamQuestionsModalOpen(false)}
          onSubmit={handleUpdateExamQuestionSettings}
          data={examQuestionsData}
          setData={setExamQuestionsData}
        />

        <OCRTokenSettings
          isOpen={OCRTokenModalOpen}
          onClose={() => setOCRTokenModalOpen(false)}
          onSubmit={handleUpdateOcrTokenSettings}
          data={ocrTokenDataData}
          setData={setOcrTokenDataData}
        />
      </Grid>
    </div>
  );
}
